import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { validateAllCityURLs } from '@/utils/validation/urlValidator';
import { CheckCircle2, XCircle, AlertTriangle, TrendingUp, MapPin, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CoverageMonitoring: React.FC = () => {
  const [coverage, setCoverage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const runValidation = async () => {
      setLoading(true);
      try {
        const results = validateAllCityURLs();
        
        setCoverage({
          cities: {
            total: results.totalCities,
            working: results.workingCities.length,
            broken: results.broken404Cities.length,
            percentage: results.totalCities > 0 
              ? (results.workingCities.length / results.totalCities) * 100 
              : 0,
            brokenList: results.broken404Cities
          },
          subLandings: {
            total: results.totalCities * results.totalSpecialties,
            working: results.workingSubLandings,
            broken: results.broken404SubLandings.length,
            percentage: (results.totalCities * results.totalSpecialties) > 0
              ? (results.workingSubLandings / (results.totalCities * results.totalSpecialties)) * 100
              : 0,
            brokenList: results.broken404SubLandings
          },
          specialtiesPerCity: results.totalSpecialties
        });
      } catch (error) {
        console.error('Error running validation:', error);
      } finally {
        setLoading(false);
      }
    };

    runValidation();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Validating URLs...</p>
        </div>
      </div>
    );
  }

  if (!coverage) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Alert variant="destructive" className="max-w-lg">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load coverage data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const targetCities = 32717;
  const targetSubLandings = 654340;
  const progressToTarget = (coverage.cities.total / targetCities) * 100;

  return (
    <>
      <Helmet>
        <title>Coverage Monitoring - SEO Validation Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-subtle py-12">
        <div className="container max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">SEO Coverage Monitoring</h1>
            <p className="text-lg text-muted-foreground">
              Real-time validation of all city landings and specialty sub-landings
            </p>
          </div>

          {/* Overall Status Alert */}
          {coverage.cities.broken === 0 && coverage.subLandings.broken === 0 ? (
            <Alert className="mb-8 border-green-500 bg-green-50 dark:bg-green-950">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <AlertTitle className="text-green-900 dark:text-green-100">All URLs Validated Successfully!</AlertTitle>
              <AlertDescription className="text-green-800 dark:text-green-200">
                All {coverage.cities.total.toLocaleString()} city landings and {coverage.subLandings.total.toLocaleString()} specialty sub-landings are working correctly.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive" className="mb-8">
              <XCircle className="h-5 w-5" />
              <AlertTitle>Validation Issues Detected</AlertTitle>
              <AlertDescription>
                Found {coverage.cities.broken + coverage.subLandings.broken} broken URLs that need attention.
              </AlertDescription>
            </Alert>
          )}

          {/* Progress to Target */}
          <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-6 h-6" />
                Progress to Target Coverage
              </CardTitle>
              <CardDescription>
                Target: {targetCities.toLocaleString()} cities × 20 specialties = {targetSubLandings.toLocaleString()} total landings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">City Landings</span>
                    <span className="text-sm font-bold">
                      {coverage.cities.total.toLocaleString()} / {targetCities.toLocaleString()}
                      <Badge variant="outline" className="ml-2">{progressToTarget.toFixed(2)}%</Badge>
                    </span>
                  </div>
                  <Progress value={progressToTarget} className="h-3" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Stats Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* City Landings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  City Landings
                </CardTitle>
                <CardDescription>Main city landing pages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">{coverage.cities.total.toLocaleString()}</span>
                    <Badge variant={coverage.cities.percentage === 100 ? "default" : "secondary"}>
                      {coverage.cities.percentage.toFixed(2)}% functional
                    </Badge>
                  </div>
                  <Progress value={coverage.cities.percentage} className="h-2" />
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <div>
                        <div className="text-sm text-muted-foreground">Working</div>
                        <div className="font-semibold">{coverage.cities.working.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <div>
                        <div className="text-sm text-muted-foreground">404 Errors</div>
                        <div className="font-semibold">{coverage.cities.broken.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specialty Sub-Landings Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Specialty Sub-Landings
                </CardTitle>
                <CardDescription>
                  {coverage.specialtiesPerCity} specialties per city
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">{coverage.subLandings.total.toLocaleString()}</span>
                    <Badge variant={coverage.subLandings.percentage === 100 ? "default" : "secondary"}>
                      {coverage.subLandings.percentage.toFixed(2)}% functional
                    </Badge>
                  </div>
                  <Progress value={coverage.subLandings.percentage} className="h-2" />
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <div>
                        <div className="text-sm text-muted-foreground">Working</div>
                        <div className="font-semibold">{coverage.subLandings.working.toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <div>
                        <div className="text-sm text-muted-foreground">404 Errors</div>
                        <div className="font-semibold">{coverage.subLandings.broken.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Error Lists */}
          {(coverage.cities.broken > 0 || coverage.subLandings.broken > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  Broken URLs Detail
                </CardTitle>
                <CardDescription>
                  URLs that returned 404 or could not be validated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="cities">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="cities">
                      Cities ({coverage.cities.broken})
                    </TabsTrigger>
                    <TabsTrigger value="specialties">
                      Specialties ({coverage.subLandings.broken})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="cities" className="mt-4">
                    {coverage.cities.brokenList.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No broken city URLs</p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {coverage.cities.brokenList.map((citySlug: string, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <code className="text-sm">/city/{citySlug}</code>
                            <Badge variant="destructive">404</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="specialties" className="mt-4">
                    {coverage.subLandings.brokenList.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No broken specialty URLs</p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {coverage.subLandings.brokenList.slice(0, 100).map((path: string, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <code className="text-sm">/city/{path}</code>
                            <Badge variant="destructive">404</Badge>
                          </div>
                        ))}
                        {coverage.subLandings.brokenList.length > 100 && (
                          <p className="text-center text-sm text-muted-foreground py-4">
                            ... and {coverage.subLandings.brokenList.length - 100} more
                          </p>
                        )}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-4 mt-8">
            <Button onClick={() => window.location.reload()}>
              Refresh Validation
            </Button>
            <Button variant="outline" onClick={() => navigate('/dev/complete-sitemap')}>
              Generate Complete Sitemap
            </Button>
            <Button variant="outline" onClick={() => navigate('/cities')}>
              View City Directory
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoverageMonitoring;
